import type {
    MouseEvent as ReactMouseEvent,
    TouchEvent as ReactTouchEvent,
    PropsWithChildren,
    HTMLAttributes
} from "react";
import { createRef, useEffect, useRef } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { useTabsContext, TabFC } from "./";

export type TabListProps = PropsWithChildren<{
    tabSize?: "small" | "large";
}> &
    HTMLAttributes<HTMLUListElement>;

const DraggableTabList: TabFC<TabListProps> = ({
    tabSize = "large",
    children
}: TabListProps) => {
    const containerRef = createRef<HTMLDivElement>();
    const mouseDown = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);
    const canDrag = useRef(false);
    const { onKeyDown, tabListIsVisible, tabListScrollRef } = useTabsContext();

    const startDragging = (
        mouse?: ReactMouseEvent<HTMLUListElement>,
        touch?: ReactTouchEvent<HTMLUListElement>
    ) => {
        if (!canDrag.current) {
            return;
        }
        // Start listening for mouseup and mousemove events outside of the tab list
        document.addEventListener("mouseup", stopDragging);
        document.addEventListener("mousemove", move);

        mouseDown.current = true;
        startX.current =
            (touch && touch.touches[0]
                ? touch.touches[0].clientX
                : mouse?.clientX) ?? 0;
        scrollLeft.current = containerRef.current?.scrollLeft ?? 0;
    };

    const stopDragging = () => {
        if (!canDrag.current) {
            return;
        }
        // Stop listening for mouseup and mousemove events
        document.removeEventListener("mouseup", stopDragging);
        document.removeEventListener("mousemove", move);

        mouseDown.current = false;
    };

    const move = (
        mouse?: MouseEvent,
        touch?: ReactTouchEvent<HTMLUListElement>
    ) => {
        if (!canDrag.current || !mouseDown.current || !containerRef.current) {
            return;
        }
        let dragAmount = 0;
        if (touch && touch.changedTouches[0]) {
            dragAmount = touch.changedTouches[0].clientX - startX.current;
        } else if (mouse) {
            dragAmount = mouse.clientX - startX.current;
        }

        containerRef.current.scrollLeft = scrollLeft.current - dragAmount;
    };

    // on resize, check if we can drag
    useEffect(() => {
        const updateCanDrag = () => {
            if (containerRef.current) {
                const container = containerRef.current;
                canDrag.current = container.scrollWidth > container.clientWidth;
            }
        };

        const setStartingPoint = () => {
            if (tabSize === "small") {
                return;
            }
            if (containerRef.current) {
                const tabPanels = document.querySelector(".tab-panels");
                const tabPanelsStyle =
                    tabPanels && window.getComputedStyle(tabPanels);
                containerRef.current.setAttribute(
                    "style",
                    `padding-left: calc(${tabPanelsStyle?.marginLeft} + ${tabPanelsStyle?.paddingLeft});`
                );
            }
        };

        if (!containerRef.current) {
            return;
        }
        updateCanDrag();
        setStartingPoint();

        const observer = new ResizeObserver(() => {
            updateCanDrag();
            setStartingPoint();
        });

        observer.observe(containerRef.current);
        return () => {
            // Cleanup the observer by unobserving all elements
            observer.disconnect();
        };
    }, [containerRef]);

    const wrapperStyle = css({
        overflowX: "hidden"
    });

    return (
        <div ref={containerRef} css={wrapperStyle}>
            <div
                ref={tabListScrollRef}
                css={[
                    tabsContainerStyles,
                    canDrag.current && tabListIsVisible && backAndForthAnimation
                ]}
                onTouchStart={(e) => startDragging(undefined, e)}
                onMouseDown={(e) => startDragging(e)}
                onTouchEnd={(e) => {
                    move(undefined, e);
                    stopDragging();
                }}
                onTouchMove={(e) => move(undefined, e)}
                onKeyDown={onKeyDown}
                className="tab-list"
                tabIndex={-1}
                role="tablist"
            >
                {children}
            </div>
        </div>
    );
};

const tabsContainerStyles = css({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    flexWrap: "nowrap",
    padding: 0,
    margin: 0,
    listStyle: "none",
    width: "fit-content",
    maxWidth: "100%",
    marginBlock: token("space.200"),
    gap: token("space.200")
});

// animation to demonstrate to user that section is draggable
const backAndForthAnimation = css({
    "@keyframes backAndForth": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "0%": {
            transform: "translateX(0)"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "50%": {
            transform: "translateX(calc(-60px))"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "100%": {
            transform: "translateX(0)"
        }
    },
    "animation": "2s backAndForth forwards .5s"
});

DraggableTabList.tabsRole = "TabList";

export { DraggableTabList };
